/*
 * The product gallery, and the fullscreen viewer behind it.
 *
 * A scroll-snap track of photographs with every control drawn OVER the picture
 * and nothing under it: arrows on the sides, dots along the bottom, a zoom mark
 * in the corner, and the whole frame a target that opens the viewer. That is
 * the rule the whole thing is built on rather than a detail of it — a thumbnail
 * rail under the frame would push the price and the first action down the page
 * by its own height on every product, and the fold budget the two heroes are
 * built around does not have that to spend.
 *
 * ── Two pages, one element ───────────────────────────────────────────────
 * The product page renders its slides in Liquid and never touches them again.
 * A landing page cannot: it does not know its product until the customer has
 * typed a registration, and it learns a different one every time they press a
 * version chip. There, `airflux-fitment.js` calls `setMedia()` with the whole
 * media list of whatever it resolved to, and `setMedia(null)` to put the
 * generic slides back when the answer goes away again.
 *
 * That is the only difference between the two, and it is one method.
 *
 * ── The fullscreen viewer ────────────────────────────────────────────────
 * Shopify's dialog, our presentation. `<modal-dialog>` from `global.js` owns
 * the focus trap, the Escape key, the body scroll lock and returning focus to
 * the opener. What it contains is a SECOND horizontal carousel — the same
 * gesture as the gallery it came from, larger, with a counter. Dawn's own
 * `product-media-modal` stacks the images vertically instead, which asks a
 * customer who just swiped sideways to start scrolling down.
 *
 * Both carousels are the same `Carousel` below. It knows how to move a snap
 * track and which slide is nearest; each owner decides what to do with that —
 * dots here, a counter there. Written twice they drifted immediately.
 *
 * The theme renders the viewer empty — see `airflux-gallery-modal.liquid` for
 * why — and this file fills it from the slides currently in the track, the
 * first time somebody opens it. The viewer's slides are therefore the gallery's
 * slides in the same order, which is what lets the button that was pressed be
 * matched to the picture to open on by POSITION. Dawn pairs those two ends with
 * a `data-media-id` on each, because its viewer is server-rendered and the two
 * lists are built by different code; here one list is built from the other.
 *
 * The tracks scroll with touch, trackpad and keyboard before any of this runs.
 * Every control is an enhancement over something already usable, and each stays
 * hidden until the element is upgraded.
 */
(() => {
  if (customElements.get('airflux-gallery')) return;

  // Candidate widths for an image the fitment engine hands over as a bare CDN
  // URL. The top of the range is what the viewer wants on a wide screen; the
  // bottom is what a slide wants on a phone. Anything above the file's own
  // width is dropped rather than requested — Shopify would upscale it.
  //
  // Six, and no more, on Shopify's own performance guidance: every extra
  // candidate is another URL the CDN caches separately and another line the
  // browser has to weigh. The same six are written into both heroes' Liquid,
  // so a slide and its viewer image share one srcset.
  const WIDTHS = [400, 600, 800, 1100, 1600, 2048];

  // What the viewer asks the browser for: one image, the width of the screen.
  const MODAL_SIZES = '100vw';

  const escapeAttr = (s) => String(s ?? '').replace(/"/g, '&quot;').replace(/</g, '&lt;');

  /** A srcset across `WIDTHS` for one Shopify CDN URL. */
  function srcsetFor(src, width) {
    const url = String(src);
    const join = url.includes('?') ? '&' : '?';
    // An image narrower than the smallest candidate would filter down to an
    // empty list, and an empty `srcset` leaves the browser nothing to choose —
    // so it always keeps one.
    const fits = WIDTHS.filter((w) => !width || w <= width);
    const chosen = fits.length ? fits : [WIDTHS[0]];
    return chosen.map((w) => `${url}${join}width=${w} ${w}w`).join(', ');
  }

  /**
   * A horizontal snap track with a pair of arrows.
   *
   * Owns moving and measuring, and nothing about what is being moved: the
   * gallery paints dots from `onChange`, the viewer paints a counter, and
   * neither knows how the other does it.
   */
  class Carousel {
    constructor(track, { prev, next, onChange, signal } = {}) {
      this.track = track;
      this.prev = prev;
      this.next = next;
      this.onChange = onChange;
      this.index = 0;
      this.slides = [];

      // 🔴 Every listener carries the element's abort signal, including the one
      // on `window`. Without it a gallery that is torn down and rebuilt — a
      // combined-listing swap does exactly that, and the theme editor does it on
      // every setting change — leaves a resize handler behind holding a detached
      // track, and the next resize runs one per rebuild.
      this.prev?.addEventListener('click', () => this.step(-1), { signal });
      this.next?.addEventListener('click', () => this.step(1), { signal });
      this.track.addEventListener('scroll', () => this.queueSync(), { passive: true, signal });
      window.addEventListener('resize', () => this.queueSync(), { passive: true, signal });
    }

    /** Re-read the track after its contents changed. */
    refresh(selector) {
      this.slides = [...this.track.querySelectorAll(selector)];
      this.sync();
    }

    get behaviour() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    }

    goTo(index, behaviour) {
      const slide = this.slides[index];
      if (!slide) return;
      // scrollLeft rather than scrollIntoView: the latter also scrolls the page
      // to bring the track into view, which yanks the whole document whenever
      // a car resolves further down.
      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: behaviour ?? this.behaviour,
      });
    }

    step(direction) {
      this.goTo(Math.min(Math.max(this.index + direction, 0), this.slides.length - 1));
    }

    queueSync() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.sync());
    }

    /** Which slide is nearest the track's left edge, and everything that follows. */
    sync() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const origin = this.track.offsetLeft;

      let nearest = 0;
      let best = Infinity;
      for (const [index, slide] of this.slides.entries()) {
        const distance = Math.abs(slide.offsetLeft - origin - scrollLeft);
        if (distance < best) {
          best = distance;
          nearest = index;
        }
      }
      this.index = nearest;

      if (this.prev) this.prev.disabled = scrollLeft <= 1;
      if (this.next) this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
      this.still = scrollWidth <= clientWidth + 1;
      this.onChange?.(nearest, this.slides.length);
    }

    destroy() {
      cancelAnimationFrame(this.frame);
    }
  }

  /**
   * Pinch, drag and double-tap on the fullscreen photograph.
   *
   * ── Why this is hand-written, when nothing else here is ──────────────────
   * There is no CSS or HTML that scopes a pinch to an element. `touch-action`
   * can only permit or forbid the BROWSER's page zoom; it cannot redirect it at
   * a picture. So a two-finger gesture over the viewer magnified the whole page
   * — the close button, the counter and the chevrons with it — and let the
   * customer drag off the photograph onto whatever was behind it. On a store
   * whose traffic is mostly phones, that is the wrong answer to the most
   * natural gesture there is, so this file owns the gesture instead.
   *
   * Owning it is all-or-nothing, and that is the cost. `touch-action: none` is
   * what routes a pinch into JavaScript, and it switches off the browser's
   * panning on the same element — so the swipe BETWEEN photographs had to come
   * here too. It is not re-implemented: a one-finger drag writes
   * `track.scrollLeft` directly, which is still the same native scroller the
   * chevrons and the keyboard drive, and letting go hands back to
   * `Carousel.goTo` to settle it. Scroll snapping is suspended for the length of
   * the drag only, because mandatory snap fights a scroll position being set by
   * hand.
   *
   * ── The one piece of arithmetic worth reading ────────────────────────────
   * `zoomTo` keeps the point under the fingers under the fingers. Convert that
   * screen point into image space at the CURRENT scale, change the scale, then
   * solve for the translation that puts the same image point back under the
   * same screen point. Every gesture here routes through it — pinch, double tap,
   * click, trackpad — so all four feel placed rather than centred.
   *
   * Everything is transform-only, so the whole gesture stays on the compositor
   * and never touches layout.
   */
  const MAX_SCALE = 4;
  // Below this a scale is "not zoomed": floating point never lands on 1 exactly
  // after a pinch, and a photograph left at 1.0001 would keep the pan handler,
  // the zoom-out cursor and the suspended swipe.
  const ZOOMED = 1.01;
  const TAP_MS = 300;
  const TAP_SLOP = 8;

  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
  const spread = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const middle = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  class Zoomer {
    constructor(track, carousel, signal) {
      this.track = track;
      this.carousel = carousel;
      this.pointers = new Map();
      this.scale = 1;
      this.x = 0;
      this.y = 0;
      this.lastTap = 0;
      // What the last gesture was, for the click listener that runs after it —
      // see the note there for why it cannot ask the click event itself.
      // 'mouse' by default so a click that somehow arrives without a
      // pointerdown still zooms rather than being silently dropped.
      this.lastType = 'mouse';
      this.lastMoved = false;

      // The viewer lives in `<body>`, moved there by `ModalDialog`, so it
      // OUTLIVES the gallery that built it. Without the signal a rebuilt
      // gallery would add a second Zoomer to the same track and both would
      // answer the same pinch.
      track.addEventListener('pointerdown', (event) => this.down(event), { signal });
      track.addEventListener('pointermove', (event) => this.move(event), { signal });
      track.addEventListener('pointerup', (event) => this.up(event), { signal });
      track.addEventListener('pointercancel', (event) => this.up(event, true), { signal });
      // Not passive: a trackpad pinch arrives as a ctrl-wheel, and letting it
      // through would zoom the page underneath the one we just zoomed.
      track.addEventListener('wheel', (event) => this.wheel(event), { passive: false, signal });
    }

    get image() {
      return this.slide?.querySelector('img') ?? null;
    }

    /** Back to fitted, and back to being the carousel's business. */
    reset() {
      const image = this.image;
      if (image) image.style.transform = '';
      this.slide?.classList.remove('is-zoomed');
      this.scale = 1;
      this.x = 0;
      this.y = 0;
      this.pinch = null;
    }

    /** Whichever photograph the gesture started on. */
    adopt(target) {
      const slide = target.closest?.('[data-lightbox-slide]');
      if (!slide || slide === this.slide) return slide ?? null;
      this.reset();
      this.slide = slide;
      return slide;
    }

    /**
     * How far the picture may travel before its edge comes inside the screen.
     * Zero on an axis the picture does not overflow, which is what stops a
     * fitted photograph sliding around under a finger.
     */
    limits() {
      const image = this.image;
      if (!image) return { x: 0, y: 0 };
      const frame = this.slide.getBoundingClientRect();
      return {
        x: Math.max(0, (image.offsetWidth * this.scale - frame.width) / 2),
        y: Math.max(0, (image.offsetHeight * this.scale - frame.height) / 2),
      };
    }

    paint() {
      const image = this.image;
      if (!image) return;
      const limit = this.limits();
      this.x = clamp(this.x, -limit.x, limit.x);
      this.y = clamp(this.y, -limit.y, limit.y);
      image.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
      this.slide.classList.toggle('is-zoomed', this.scale > ZOOMED);
    }

    /** Scale to `next`, holding the image point under (cx, cy) in place. */
    zoomTo(next, cx, cy) {
      if (!this.image) return;
      const frame = this.slide.getBoundingClientRect();
      const originX = frame.left + frame.width / 2;
      const originY = frame.top + frame.height / 2;

      const px = (cx - originX - this.x) / this.scale;
      const py = (cy - originY - this.y) / this.scale;

      this.scale = clamp(next, 1, MAX_SCALE);
      this.x = cx - originX - px * this.scale;
      this.y = cy - originY - py * this.scale;
      this.paint();
    }

    toggle(cx, cy) {
      if (this.scale > ZOOMED) {
        this.scale = 1;
        this.x = 0;
        this.y = 0;
        this.paint();
      } else {
        this.zoomTo(2, cx, cy);
      }
    }

    // ── the gestures ─────────────────────────────────────────────────────────

    down(event) {
      if (!this.adopt(event.target)) return;
      this.lastType = event.pointerType;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      // Capture keeps the moves coming when a finger leaves the picture — a
      // pinch that starts near an edge otherwise stops reporting halfway
      // through. It THROWS for a pointer the browser no longer considers
      // active, so the gesture state above is recorded first and the call is
      // allowed to fail: without capture the gesture still works, it just ends
      // early at the boundary.
      try {
        this.track.setPointerCapture(event.pointerId);
      } catch {
        /* the pointer went away between the event and this line */
      }

      if (this.pointers.size === 1) {
        this.drag = {
          x: event.clientX,
          y: event.clientY,
          tx: this.x,
          ty: this.y,
          scrollLeft: this.track.scrollLeft,
          type: event.pointerType,
          time: event.timeStamp,
          moved: false,
        };
        return;
      }

      if (this.pointers.size === 2) this.beginPinch();
    }

    beginPinch() {
      const [a, b] = [...this.pointers.values()];
      const mid = middle(a, b);
      const frame = this.slide.getBoundingClientRect();
      const originX = frame.left + frame.width / 2;
      const originY = frame.top + frame.height / 2;
      this.pinch = {
        spread: spread(a, b) || 1,
        scale: this.scale,
        // The point between the fingers, in image space, fixed for the whole
        // gesture — so the picture pivots on it rather than drifting.
        px: (mid.x - originX - this.x) / this.scale,
        py: (mid.y - originY - this.y) / this.scale,
      };
      this.track.classList.add('is-gesturing');
    }

    move(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size >= 2 && this.pinch) {
        const [a, b] = [...this.pointers.values()];
        const mid = middle(a, b);
        const frame = this.slide.getBoundingClientRect();
        const originX = frame.left + frame.width / 2;
        const originY = frame.top + frame.height / 2;

        this.scale = clamp((this.pinch.scale * spread(a, b)) / this.pinch.spread, 1, MAX_SCALE);
        this.x = mid.x - originX - this.pinch.px * this.scale;
        this.y = mid.y - originY - this.pinch.py * this.scale;
        this.paint();
        return;
      }

      if (!this.drag) return;
      const dx = event.clientX - this.drag.x;
      const dy = event.clientY - this.drag.y;
      if (!this.drag.moved && Math.hypot(dx, dy) > TAP_SLOP) {
        this.drag.moved = true;
        this.track.classList.add('is-gesturing');
      }
      if (!this.drag.moved) return;

      if (this.scale > ZOOMED) {
        this.x = this.drag.tx + dx;
        this.y = this.drag.ty + dy;
        this.paint();
      } else if (this.drag.type !== 'mouse') {
        // Fitted: the drag belongs to the carousel. Writing scrollLeft keeps
        // ONE scroller — the same one the chevrons, the keyboard and `goTo`
        // move — rather than a second idea of which photograph is showing.
        this.track.scrollLeft = this.drag.scrollLeft - dx;
      }
    }

    up(event, cancelled) {
      this.pointers.delete(event.pointerId);

      // A finger lifted out of a pinch leaves the other one panning, so the
      // drag baseline restarts from where that finger is now.
      if (this.pointers.size === 1) {
        const [held] = [...this.pointers.values()];
        this.drag = {
          x: held.x,
          y: held.y,
          tx: this.x,
          ty: this.y,
          scrollLeft: this.track.scrollLeft,
          type: event.pointerType,
          time: event.timeStamp,
          moved: true,
        };
        this.pinch = null;
        return;
      }
      if (this.pointers.size > 0) return;

      this.track.classList.remove('is-gesturing');
      this.pinch = null;

      // A pinch that ended below the fitted size snaps back to it rather than
      // leaving the picture a few percent small in the middle of the screen.
      if (this.scale <= ZOOMED) {
        this.scale = 1;
        this.x = 0;
        this.y = 0;
        this.paint();
      } else {
        this.paint();
      }

      const drag = this.drag;
      this.drag = null;
      // Read by the click listener, which fires straight after this one.
      this.lastMoved = drag?.moved ?? false;
      if (!drag) return;

      if (drag.moved) {
        if (this.scale <= ZOOMED && drag.type !== 'mouse') this.settle(event, drag);
        return;
      }

      // A tap that never moved. Touch waits for a second one; a mouse is
      // handled by the click listener, which fires after this and can tell a
      // click from the end of a drag.
      //
      // A CANCELLED pointer is not a tap — the browser took the gesture away
      // mid-flight, and counting it would leave half a double-tap armed, so the
      // customer's next real tap would zoom on its own.
      if (cancelled || drag.type === 'mouse') return;
      if (event.timeStamp - this.lastTap < TAP_MS) {
        this.toggle(event.clientX, event.clientY);
        this.lastTap = 0;
      } else {
        this.lastTap = event.timeStamp;
      }
    }

    /**
     * Where a released swipe lands.
     *
     * A short fast flick moves one photograph — that is the gesture people
     * actually make, and requiring half a screen of travel is what makes a
     * carousel feel stuck. Anything else goes to whichever is nearest, which is
     * what `Carousel.sync` already works out.
     */
    settle(event, drag) {
      const dx = event.clientX - drag.x;
      const elapsed = event.timeStamp - drag.time;
      this.carousel.sync();
      if (Math.abs(dx) > 40 && elapsed < 300) this.carousel.step(dx < 0 ? 1 : -1);
      else this.carousel.goTo(this.carousel.index);
    }

    /**
     * Trackpad and mouse wheel.
     *
     * A trackpad pinch reaches the page as a wheel event with `ctrlKey` set —
     * the browser's own convention, not a guess — so the same gesture works on a
     * laptop. A plain wheel pans a zoomed photograph and is left alone otherwise,
     * so it still does nothing on a fitted one.
     */
    wheel(event) {
      if (!this.adopt(event.target)) return;
      if (event.ctrlKey) {
        event.preventDefault();
        this.zoomTo(this.scale * (1 - event.deltaY / 150), event.clientX, event.clientY);
        return;
      }
      if (this.scale <= ZOOMED) return;
      event.preventDefault();
      this.x -= event.deltaX;
      this.y -= event.deltaY;
      this.paint();
    }
  }

  class AirfluxGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;

      // `connectedCallback` can run more than once — a custom element that is
      // moved rather than replaced is disconnected and reconnected — so setup
      // starts by undoing itself. Every listener this element owns, directly or
      // through `Carousel` and `Zoomer`, is bound to this one signal.
      this.teardown();
      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.dotList = this.querySelector('[data-gallery-dots]');

      // The generic slides, kept verbatim so a landing page can go back to them
      // when the customer clears their car. Only the engine-owned region is
      // stored: merchant slides after it are never replaced.
      this.defaultHtml = [...this.track.querySelectorAll('[data-gallery-product]')]
        .map((li) => li.outerHTML)
        .join('');

      this.carousel = new Carousel(this.track, {
        prev: this.querySelector('[data-gallery-prev]'),
        next: this.querySelector('[data-gallery-next]'),
        onChange: (index) => this.paintDots(index),
        signal,
      });

      this.addEventListener(
        'click',
        (event) => {
          const zoom = event.target.closest('[data-gallery-zoom]');
          if (zoom) this.openViewer(zoom);
        },
        { signal }
      );

      this.collect();

      // Slide one is the product, and it has to be what the page opens on.
      // A snap container keeps whatever offset it had across a soft navigation
      // or a restored history entry, which is how the hero was landing on the
      // second slide before anyone had touched it.
      this.track.scrollLeft = 0;

      this.setAttribute('data-ready', '');

      // The engine may have resolved a car before this element was upgraded —
      // `airflux-fitment.js` is loaded from the document head and this file
      // from the section, so on a returning visit it usually has. It leaves the
      // answer on the element rather than calling a method that does not exist
      // yet; this is where that gets picked up.
      if (this._media !== undefined) this.setMedia(this._media);
    }

    disconnectedCallback() {
      this.teardown();
    }

    /**
     * Drop every listener and pending frame this element owns.
     *
     * The viewer is the reason this has to be thorough: `ModalDialog` moves it
     * to `<body>`, so it survives the gallery and a rebuilt gallery would
     * otherwise stack a second set of gesture handlers on the same track.
     */
    teardown() {
      this.controller?.abort();
      this.controller = null;
      this.carousel?.destroy();
      this.viewer?.destroy();
      this.carousel = null;
      this.viewer = null;
      this.zoomer = null;
      this.viewerBuilt = false;
    }

    // ── the slide set ────────────────────────────────────────────────────────

    /**
     * Re-read the track and rebuild everything that counts slides.
     *
     * Called on connect and after every `setMedia`. The dots are built here
     * rather than in Liquid because on a landing page the number of them is not
     * known until a car has been resolved — and a rail of dots that does not
     * match the track is worse than no rail at all.
     */
    collect() {
      this.carousel.refresh('[data-gallery-slide]');
      const slides = this.carousel.slides;

      // A product still importing renders a placeholder with nothing to
      // enlarge, and a zoom mark over it would be a promise the frame cannot
      // keep.
      this.toggleAttribute('data-zoomable', slides.some((s) => s.querySelector('[data-gallery-zoom]')));

      if (this.dotList) {
        this.dotList.innerHTML =
          slides.length > 1
            ? slides
                .map(
                  (_, index) =>
                    `<li><button type="button" class="af-gallery__dot" data-gallery-dot ` +
                    `aria-label="${escapeAttr(`${index + 1} ${this.dotList.dataset.of || 'of'} ${slides.length}`)}"></button></li>`
                )
                .join('')
            : '';
        this.dots = [...this.dotList.querySelectorAll('[data-gallery-dot]')];
        for (const [index, dot] of this.dots.entries()) {
          dot.addEventListener('click', () => this.carousel.goTo(index));
        }
      }

      // The viewer, if it is already built, is now showing the wrong pictures.
      this.viewerBuilt = false;
      this.toggleAttribute('data-static', this.carousel.still);
      this.paintDots(this.carousel.index);
    }

    paintDots(current) {
      for (const [index, dot] of (this.dots ?? []).entries()) {
        const on = index === current;
        dot.classList.toggle('is-active', on);
        // aria-current, not aria-selected: these are buttons that move the
        // track, not tabs that swap panels.
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      }
    }

    /**
     * The slides for a client-side resolved product — or `null` for the
     * generic ones the server sent.
     *
     * @param {Array<{src: string, alt?: string, width?: number, height?: number}>|null} items
     */
    setMedia(items) {
      this._media = items;
      if (!this.track) return; // not upgraded yet; connectedCallback replays it

      const region = [...this.track.querySelectorAll('[data-gallery-product]')];
      const anchor = region.length ? region[region.length - 1].nextSibling : this.track.firstChild;

      const html = items?.length ? items.map((item, index) => this.slideHtml(item, index)).join('') : this.defaultHtml;
      if (!html) return;

      for (const li of region) li.remove();
      this.track.insertBefore(document.createRange().createContextualFragment(html), anchor);

      this.collect();
      this.carousel.goTo(0);
    }

    /**
     * One slide, in the same shape Liquid renders.
     *
     * `data-airflux-hero` stays on the first one: the pre-paint pass in
     * `airflux-pdp-hero.liquid` paints the last resolved car's photograph as
     * that element's background while the real image loads, and it is still
     * doing that at the moment this runs.
     */
    slideHtml(item, index) {
      const alt = escapeAttr(item.alt || '');
      const src = escapeAttr(item.src);
      const dimensions = item.width && item.height ? ` width="${item.width}" height="${item.height}"` : '';
      return (
        `<li class="af-gallery__slide" data-gallery-slide data-gallery-product>` +
        `<div class="af-gallery__figure"${index === 0 ? ' data-airflux-hero' : ''}>` +
        `<img class="af-gallery__img" src="${src}${item.src.includes('?') ? '&' : '?'}width=1100"` +
        ` srcset="${escapeAttr(srcsetFor(item.src, item.width))}"` +
        ` sizes="(min-width: 990px) 50vw, 100vw" alt="${alt}"${dimensions}` +
        ` ${index === 0 ? 'fetchpriority="high"' : 'loading="lazy"'}>` +
        `<button type="button" class="af-gallery__zoom" data-gallery-zoom aria-haspopup="dialog">` +
        `<span class="visually-hidden">${escapeAttr(this.zoomLabel(index + 1))}</span>` +
        `</button>` +
        `</div></li>`
      );
    }

    /**
     * "Open media 3 in modal", in the shop's own language.
     *
     * Liquid hands over the translated string with `%n` where the number goes,
     * rather than a finished label per slide: the slides a landing page shows
     * are built here, long after the last Liquid ran. `%n` and not `{{ index }}`
     * because a Liquid string literal cannot contain `{{`.
     */
    zoomLabel(position) {
      return (this.getAttribute('data-zoom-label') || 'Open media %n in modal').replace('%n', position);
    }

    // ── the fullscreen viewer ────────────────────────────────────────────────

    /**
     * Fill the viewer from the current slides, then hand it to Shopify.
     *
     * Built on demand and only once per slide set: most visitors never open it,
     * and on a landing page the right contents are not known until a car is.
     */
    openViewer(opener) {
      // 🔴 Guarded, not defaulted to ''. `querySelector('')` THROWS rather than
      // returning null, so a hero rendered without the attribute would break on
      // the first press instead of quietly doing nothing.
      const selector = this.getAttribute('data-modal');
      if (!selector) return;

      // The LAST match, not the first. `ModalDialog` moves itself to `<body>`
      // on connect, so a section that re-renders — which is every setting
      // change in the theme editor — leaves the old viewer sitting there beside
      // the new one, sharing its id. The newest is the one this gallery's
      // markup brought with it.
      const modals = document.querySelectorAll(selector);
      const modal = modals[modals.length - 1];
      if (!modal?.show) return;

      const track = modal.querySelector('[data-lightbox-track]');
      if (!track) return;

      if (!this.viewer) {
        this.count = modal.querySelector('[data-lightbox-count]');
        this.viewer = new Carousel(track, {
          prev: modal.querySelector('[data-lightbox-prev]'),
          next: modal.querySelector('[data-lightbox-next]'),
          onChange: (index, total) => {
            this.paintCount(index, total);
            // Moving to another photograph puts the zoom back. Only on an
            // actual change: this also runs on resize and after every jump,
            // and unzooming somebody mid-pan would be maddening.
            if (index !== this._viewerIndex) {
              this._viewerIndex = index;
              this.zoomer?.reset();
            }
          },
        });

        this.zoomer = new Zoomer(track, this.viewer, this.controller?.signal);

        // A mouse click zooms; a touch tap does not, because touch counts taps
        // itself in `Zoomer.up` and wants the second one.
        //
        // 🔴 It CANNOT be told apart by `event.pointerType` here. Safari still
        // dispatches `click` as a plain MouseEvent, where that property does not
        // exist — so the touch guard that read it never fired and one tap zoomed
        // on exactly the devices this is for. The pointer type is recorded on
        // `pointerdown`, which is a PointerEvent everywhere, and read back here.
        track.addEventListener('click', (event) => {
          if (this.zoomer.lastType !== 'mouse') return;
          // A click also ends a drag. Zooming on the release of a pan would
          // fire on every attempt to move a zoomed photograph.
          if (this.zoomer.lastMoved) return;
          // Guarded: a click on the track but outside any photograph would
          // otherwise toggle whichever slide was touched last.
          if (!this.zoomer.adopt(event.target)) return;
          this.zoomer.toggle(event.clientX, event.clientY);
        }, { signal: this.controller?.signal });

        // A zoom is measured against one viewport, so a resize retires it.
        window.addEventListener('resize', () => this.zoomer?.reset(), {
          passive: true,
          signal: this.controller?.signal,
        });
      }

      if (!this.viewerBuilt) {
        track.innerHTML = this.carousel.slides.map((slide) => this.viewerSlideHtml(slide)).join('');
        this.viewerBuilt = true;
        // The slide it was holding no longer exists.
        this.zoomer.slide = null;
      }

      // A viewer left zoomed when it was closed must not reopen that way.
      this.zoomer.reset();

      modal.show(opener);

      // AFTER `show()`: the dialog is `visibility: hidden` until then, and a
      // hidden element has no layout — every slide would measure at offset 0
      // and the jump would land on the first picture whatever was pressed.
      this.viewer.refresh('[data-lightbox-slide]');
      const index = this.carousel.slides.findIndex(
        (slide) => slide.querySelector('[data-gallery-zoom]') === opener
      );
      this.viewer.goTo(Math.max(index, 0), 'instant');
      this.viewer.sync();
    }

    /** One viewer slide, cloned from the gallery slide behind it. */
    viewerSlideHtml(slide) {
      const img = slide.querySelector('img');
      if (!img) return '';
      // 🔴 The ratio comes from the ATTRIBUTES, not from `naturalWidth`. Every
      // slide past the first is lazy, so most of them have never been decoded
      // when the viewer is built — and an undecoded image reports 0×0, which
      // fell back to square. Placeholders claiming 1:1 and then loading at 3:2
      // is the column jumping under the reader, which is the exact thing
      // stating a height prevents.
      const w = Number(img.getAttribute('width')) || img.naturalWidth || 1;
      const h = Number(img.getAttribute('height')) || img.naturalHeight || 1;
      return (
        `<li class="af-lightbox__slide" data-lightbox-slide>` +
        `<img class="af-lightbox__img" src="${escapeAttr(img.currentSrc || img.src)}"` +
        (img.srcset ? ` srcset="${escapeAttr(img.srcset)}"` : '') +
        ` sizes="${MODAL_SIZES}" alt="${escapeAttr(img.alt || '')}" loading="lazy"` +
        ` width="${w}" height="${h}">` +
        `</li>`
      );
    }

    /** "3 / 10", plus the sentence a screen reader should hear instead. */
    paintCount(index, total) {
      if (!this.count) return;
      // Only when it actually changed. `sync` runs once per animation frame for
      // the whole length of a swipe, and rebuilding this markup — inside an
      // `aria-live` region, which re-announces on every write — sixty times a
      // second is both wasted work and a screen reader talking over itself.
      const key = `${index}/${total}`;
      if (key === this._countKey) return;
      this._countKey = key;
      const of = this.count.dataset.of || 'of';
      this.count.innerHTML =
        `<span aria-hidden="true">${index + 1} / ${total}</span>` +
        `<span class="visually-hidden">${escapeAttr(`${index + 1} ${of} ${total}`)}</span>`;
    }
  }

  customElements.define('airflux-gallery', AirfluxGallery);

  /* ── the viewer, for anything that is not a gallery ──────────────────────────
   *
   * Same modal (`snippets/airflux-gallery-modal.liquid`), same `Carousel`, same
   * `Zoomer`, same CSS — driven from a plain list of images instead of from a
   * gallery's own slides. Review photos use it; anything else with a handful of
   * pictures and no product behind them can too.
   *
   * 🔴 It is a SECOND CALLER of the engine, not a second engine. `AirfluxGallery`
   * keeps its own `openViewer` because it has to map the pressed zoom button back
   * to a slide index and rebuild when a resolved car changes the media — neither
   * of which means anything here. If that ever stops being true, collapse the two;
   * do not let a fix land in one and not the other.
   *
   * State is per-modal rather than global: two sections can each render their own
   * viewer, and a `Carousel` is bound to the track it was built on.
   */
  const viewers = new WeakMap();

  function openLightbox({ modal, images, index = 0, opener }) {
    if (!modal?.show) return false;
    const track = modal.querySelector('[data-lightbox-track]');
    if (!track || !images?.length) return false;

    let state = viewers.get(modal);
    if (!state) {
      const count = modal.querySelector('[data-lightbox-count]');
      const carousel = new Carousel(track, {
        prev: modal.querySelector('[data-lightbox-prev]'),
        next: modal.querySelector('[data-lightbox-next]'),
        onChange: (index, total) => {
          // "3 / 2" is the whole reason this is wired: without it the viewer
          // opens with a blank counter and a customer with several photos has
          // no idea how many there are.
          //
          // Only on an actual change — `sync` runs once per animation frame for
          // the length of a swipe, and this writes into an `aria-live` region,
          // which re-announces on every write.
          // `state` is still undefined if `Carousel` calls this from its own
          // constructor, which runs before the assignment below.
          if (!count || !state || state.countKey === `${index}/${total}`) return;
          state.countKey = `${index}/${total}`;
          const of = count.dataset.of || 'of';
          count.innerHTML =
            `<span aria-hidden="true">${index + 1} / ${total}</span>` +
            `<span class="visually-hidden">${escapeAttr(`${index + 1} ${of} ${total}`)}</span>`;
          if (index !== state.index) {
            state.index = index;
            state.zoomer?.reset();
          }
        },
      });
      const zoomer = new Zoomer(track, carousel);
      // Mouse click zooms, touch tap does not — `Zoomer` counts taps itself and
      // wants the second one. The pointer type is recorded on `pointerdown`
      // because Safari dispatches `click` as a plain MouseEvent with no
      // `pointerType` at all.
      track.addEventListener('click', (event) => {
        if (zoomer.lastType !== 'mouse' || zoomer.lastMoved) return;
        if (!zoomer.adopt(event.target)) return;
        zoomer.toggle(event.clientX, event.clientY);
      });
      window.addEventListener('resize', () => zoomer.reset(), { passive: true });
      state = { carousel, zoomer, key: '', countKey: '', index: -1 };
      viewers.set(modal, state);
    }

    // Rebuilt only when the pictures actually change — reopening the same set
    // should not throw away decoded images.
    const key = images.join('|');
    if (key !== state.key) {
      track.innerHTML = images
        .map(
          (src) =>
            `<li class="af-lightbox__slide" data-lightbox-slide>` +
            `<img class="af-lightbox__img" src="${escapeAttr(src)}" alt="" loading="lazy">` +
            `</li>`
        )
        .join('');
      state.key = key;
      state.zoomer.slide = null;
    }

    state.zoomer.reset();
    modal.show(opener);
    // AFTER `show()` — the dialog is `visibility: hidden` until then, and a
    // hidden element has no layout, so every slide would measure at offset 0 and
    // the jump would land on the first picture whatever was pressed.
    state.carousel.refresh('[data-lightbox-slide]');
    state.carousel.goTo(Math.max(index, 0), 'instant');
    state.carousel.sync();
    return true;
  }

  window.AirfluxLightbox = { open: openLightbox };
})();
